import { useMemo, useState, type ReactNode } from 'react';
import { EyeOff, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useGetShippingAddOns,
  useUpdateShippingAddOn,
  useCustomizePlatformShippingAddOn,
  useDeleteShippingAddOn,
  useHidePlatformShippingAddOn,
  useHideShippingAddOn,
  useHideShippingAddOnCategory,
} from '~/api/logistics';
import { Button } from '~/components/ui/buttons/Button';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import {
  Table,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table/Table';
import { useTable } from '~/components/ui/table';
import { CURRENCIES } from '~/components/ui/inputs/CurrencySelector';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';
import { AddOnCostModal, type AddOnModalMode } from './AddOnCostModal';
import { HiddenAddOnsModal } from './HiddenAddOnsModal';
import type {
  GetShippingAddOnsQuery,
  UpdateShippingAddOnInput,
  AddOnBillingUnit,
  Currency,
} from '~/graphql/graphql';

type ShippingAddOn = GetShippingAddOnsQuery['shippingAddOns'][number];

const BILLING_UNIT_OPTIONS: AddOnBillingUnit[] = [
  'CONTAINER',
  'BL',
  'PERCENT_MATERIAL_VALUE',
  'BL_STD_CONTAINER',
  'CONTAINER_EQUIV',
  'TRUCKLOAD',
  'FLATRACK_CONTAINER',
  'BASE_UOM',
];

function ActiveBadge({
  active,
  label,
}: {
  active: boolean;
  label: string;
}): ReactNode {
  return (
    <span
      className={`text-xs font-medium px-2 py-1 rounded-lg whitespace-nowrap ${
        active ? 'text-success-700 bg-success-100' : 'text-neut-500 bg-neut-100'
      }`}
    >
      {label}
    </span>
  );
}

function CustomBadge({ label }: { label: string }): ReactNode {
  return (
    <span className="text-xs font-medium px-2 py-1 rounded-lg whitespace-nowrap text-blue-700 bg-blue-100 ml-2">
      {label}
    </span>
  );
}

export function AddOnCostsTable() {
  const { t } = useTranslation('logistics');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<AddOnModalMode>('create');
  const [selectedAddOn, setSelectedAddOn] = useState<
    ShippingAddOn | undefined
  >();
  const [hiddenModalOpen, setHiddenModalOpen] = useState(false);

  const { data, isLoading } = useGetShippingAddOns({ hidden: false });
  const { data: hiddenData } = useGetShippingAddOns({ hidden: true });
  const { mutate: updateAddOn } = useUpdateShippingAddOn();
  const { mutate: overrideAddOn } = useCustomizePlatformShippingAddOn();
  const { mutate: deleteAddOn } = useDeleteShippingAddOn();
  const { mutate: hidePlatformAddOn } = useHidePlatformShippingAddOn();
  const { mutate: hideAddOn } = useHideShippingAddOn();
  const { mutate: hideCategory } = useHideShippingAddOnCategory();

  const hiddenCount = hiddenData?.shippingAddOns.length ?? 0;

  const groups = useMemo(() => {
    const map = new Map<string, ShippingAddOn[]>();
    for (const addOn of data?.shippingAddOns ?? []) {
      const key = addOn.category;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(addOn);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const addOns = data?.shippingAddOns ?? [];

  const openModal = (mode: AddOnModalMode, addOn?: ShippingAddOn) => {
    setModalMode(mode);
    setSelectedAddOn(addOn);
    setModalOpen(true);
  };

  const handleToggleActive = (addOn: ShippingAddOn) => {
    const input: UpdateShippingAddOnInput = { active: !addOn.active };
    const handlers = {
      onError: (error: ClientError) =>
        toast.error(extractGraphQLErrorMessages(error).join(', ')),
    };
    if (addOn.organizationId === null) {
      overrideAddOn({ platformAddOnId: addOn.id, input }, handlers);
    } else {
      updateAddOn({ id: addOn.id, input }, handlers);
    }
  };

  const handleReset = (addOn: ShippingAddOn) => {
    deleteAddOn(
      { id: addOn.id },
      {
        onSuccess: () =>
          toast.success(
            addOn.isPlatformCustomization
              ? t('logistics.add_on_costs.reset_to_default')
              : t('logistics.add_on_costs.modal.success_delete')
          ),
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );
  };

  const handleHide = (addOn: ShippingAddOn) => {
    const handlers = {
      onSuccess: () => toast.success(t('logistics.add_on_costs.hide_success')),
      onError: (error: ClientError) =>
        toast.error(extractGraphQLErrorMessages(error).join(', ')),
    };
    if (addOn.organizationId === null) {
      hidePlatformAddOn({ platformAddOnId: addOn.id }, handlers);
    } else {
      hideAddOn({ id: addOn.id }, handlers);
    }
  };

  const handleHideCategory = (category: string) => {
    hideCategory(
      { category },
      {
        onSuccess: (result) =>
          toast.success(
            t('logistics.add_on_costs.hide_category_success', {
              count: result.hideShippingAddOnCategory,
            })
          ),
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );
  };

  const tableControls = useTable({
    onEditSubmit: (rowId, columnKey, newValue) => {
      const addOn = addOns.find((a) => a.id === rowId);
      if (!addOn) return;

      let input: UpdateShippingAddOnInput;
      if (columnKey === 'cost') {
        input = {
          cost: { amount: Number(newValue), currency: addOn.currency },
        };
      } else if (columnKey === 'currency') {
        input = {
          cost: { amount: addOn.cost, currency: String(newValue) as Currency },
        };
      } else if (columnKey === 'billingUnit') {
        input = { billingUnit: String(newValue) as AddOnBillingUnit };
      } else {
        input = { description: String(newValue) };
      }

      const handlers = {
        onSuccess: () =>
          toast.success(t('logistics.add_on_costs.modal.success_edit')),
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
        onSettled: () => tableControls.handleEditDone(),
      };

      if (addOn.organizationId === null) {
        overrideAddOn({ platformAddOnId: rowId, input }, handlers);
      } else {
        updateAddOn({ id: rowId, input }, handlers);
      }
    },
  });

  const columns: TableColumn[] = [
    {
      key: 'name',
      header: t('logistics.add_on_costs.table.name'),
      width: 12,
      textShade: 'dark',
    },
    {
      key: 'cost',
      header: t('logistics.add_on_costs.table.cost'),
      isEditable: true,
      editableType: 'number',
      width: 9,
    },
    {
      key: 'currency',
      header: t('logistics.common.currency'),
      isEditable: true,
      editableType: 'select',
      editableOptions: CURRENCIES.map((c) => ({ value: c, label: c })),
      width: 7,
      textShade: 'light',
    },
    {
      key: 'billingUnit',
      header: t('logistics.add_on_costs.table.billing_unit'),
      isEditable: true,
      editableType: 'select',
      editableOptions: BILLING_UNIT_OPTIONS.map((o) => ({
        value: o,
        label: t(`logistics.add_on_costs.billing_unit.${o}`),
      })),
      width: 10,
      textShade: 'light',
    },
    {
      key: 'description',
      header: t('logistics.add_on_costs.modal.description'),
      isEditable: true,
      editableType: 'text',
      maxWidth: 20,
      textShade: 'light',
    },
    {
      key: 'active',
      header: t('logistics.add_on_costs.table.active'),
      width: 7,
    },
    {
      key: 'actions',
      header: '',
      width: 4,
      stickTo: 'right',
    },
  ];

  const buildAddOnRow = (addOn: ShippingAddOn): TableRow => {
    const isCustom = addOn.organizationId !== null;
    const isPlatformOrCustomization =
      !isCustom || addOn.isPlatformCustomization;
    const resetLabel = addOn.isPlatformCustomization
      ? t('logistics.add_on_costs.reset_to_default')
      : t('logistics.add_on_costs.delete');
    const hideLabel = t('logistics.add_on_costs.hide');
    return {
      id: { value: addOn.id },
      name: {
        value: (
          <span className="flex items-center">
            {addOn.name}
            {isCustom && (
              <CustomBadge label={t('logistics.add_on_costs.custom_badge')} />
            )}
          </span>
        ),
      },
      cost: {
        value: addOn.cost.toLocaleString(),
        editValue: addOn.cost,
      },
      currency: {
        value: addOn.currency,
        editValue: addOn.currency,
      },
      billingUnit: {
        value: t(`logistics.add_on_costs.billing_unit.${addOn.billingUnit}`),
        editValue: addOn.billingUnit,
      },
      description: {
        value: addOn.description ?? '',
        editValue: addOn.description ?? '',
      },
      active: {
        value: (
          <button
            onClick={() => handleToggleActive(addOn)}
            title={t(`logistics.add_on_costs.active.${!addOn.active}`)}
          >
            <ActiveBadge
              active={addOn.active}
              label={t(`logistics.add_on_costs.active.${addOn.active}`)}
            />
          </button>
        ),
      },
      actions: {
        value: (
          <div className="flex items-center gap-2 justify-end">
            {isCustom && (
              <button
                type="button"
                title={resetLabel}
                aria-label={resetLabel}
                className="text-neut-400 hover:text-danger-600 transition-colors"
                onClick={() => handleReset(addOn)}
                data-testid={`addon-reset-${addOn.id}`}
              >
                {addOn.isPlatformCustomization ? (
                  <RotateCcw size={14} />
                ) : (
                  <Trash2 size={14} />
                )}
              </button>
            )}
            {isPlatformOrCustomization && (
              <button
                type="button"
                title={hideLabel}
                aria-label={hideLabel}
                className="text-neut-400 hover:text-danger-600 transition-colors"
                onClick={() => handleHide(addOn)}
                data-testid={`addon-hide-${addOn.id}`}
              >
                <EyeOff size={14} />
              </button>
            )}
          </div>
        ),
      },
    };
  };

  const buildSectionHeader = (category: string, items: ShippingAddOn[]) => {
    const hasHideable = items.some(
      (a) => a.organizationId === null || a.isPlatformCustomization
    );
    const hideCategoryLabel = t('logistics.add_on_costs.hide_category');
    return (
      <div className="flex items-center gap-2">
        <span>{category}</span>
        {hasHideable && (
          <button
            type="button"
            title={hideCategoryLabel}
            aria-label={hideCategoryLabel}
            className="text-neut-400 hover:text-danger-600 transition-colors"
            onClick={() => handleHideCategory(category)}
            data-testid={`addon-hide-category-${category}`}
          >
            <EyeOff size={14} />
          </button>
        )}
      </div>
    );
  };

  const rows: TableRow[] = [];
  for (const [type, groupAddOns] of groups) {
    rows.push({
      id: { value: `__header__${type}` },
      _sectionHeader: buildSectionHeader(type, groupAddOns),
    });
    for (const addOn of groupAddOns) {
      rows.push(buildAddOnRow(addOn));
    }
  }

  return (
    <>
      <AddOnCostModal
        key={`${modalMode}-${selectedAddOn?.id ?? 'new'}`}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        addOn={selectedAddOn}
      />
      <HiddenAddOnsModal
        isOpen={hiddenModalOpen}
        onClose={() => setHiddenModalOpen(false)}
      />

      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neut-prim">
              {t('logistics.add_on_costs.title')}
            </h2>
            <p className="text-xs text-neut-sec mt-1">
              {t('logistics.add_on_costs.subtitle')}
            </p>
          </div>
          <Button size="sm" width="auto" onClick={() => openModal('create')}>
            <Plus size={16} className="mr-1" />
            {t('logistics.add_on_costs.add_cost')}
          </Button>
        </div>

        {groups.length === 0 && !isLoading && (
          <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm px-6 py-10 text-center text-sm text-neut-sec">
            {t('logistics.add_on_costs.empty')}
          </div>
        )}

        {rows.length > 0 && (
          <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
            <Table
              columns={columns}
              rows={rows}
              tableControls={tableControls}
            />
          </div>
        )}

        {hiddenCount > 0 && !isLoading && (
          <div className="flex justify-center py-2">
            <button
              type="button"
              className="text-sm text-neut-500 hover:text-prim underline-offset-2 hover:underline"
              onClick={() => setHiddenModalOpen(true)}
              data-testid="addon-hidden-toggle"
            >
              {t('logistics.add_on_costs.hidden_count', {
                count: hiddenCount,
              })}
            </button>
          </div>
        )}

        {isLoading && <PageLoadingSpinner name="add-ons" />}
      </div>
    </>
  );
}
